// Shared AuthContext interface for frontend and mobile consistency
// This ensures both platforms use the same authentication patterns
//
// AuthUser is schema-authoritative — the TS type is derived via z.infer<>.
// AuthContextType stays a plain interface: it carries
// method-typed members (signIn/signUp/signOut/...) that Zod can't cleanly
// express, so it is intentionally left un-migrated.

import { z } from 'zod';

/**
 * Generic user interface for authentication contexts
 * Compatible with Firebase Auth, mock auth, and other auth systems
 */
export const AuthUserSchema = z.object({
  uid: z.string(),
  email: z.string().nullable(),
  displayName: z.string().nullable(),
  photoURL: z.string().nullable(),
});

export type AuthUser = z.infer<typeof AuthUserSchema>;

/**
 * Shared authentication context interface
 * Used by both frontend and mobile applications
 */
export interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ user: AuthUser | null; error?: string }>;
  signUp: (
    email: string,
    password: string,
    displayName?: string,
  ) => Promise<{ user: AuthUser | null; error?: string }>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  updateProfile?: (updates: { displayName?: string; photoURL?: string }) => Promise<void>;
}

// --- Academic email eligibility ---
//
// Q4uDoc admits US-licensed clinicians (with an NPI) OR people with an academic email. This is the
// single definition of the second half of that gate; it previously existed as separate client and
// server copies, which were equivalent but free to drift once a whitelist lands.
//
// The server's use is the enforcing one: PUT /users/me strips `eduEmail`, so
// POST /auth/send-student-verification is its only writer. The client's is UX only.

/** Accepted academic email suffixes. Replace with full domains to turn this into a whitelist. */
export const ACADEMIC_EMAIL_SUFFIXES: readonly string[] = ['.edu'];

/** Note this does NOT admit `user@x.edu.au` — that ends in `.au`. */
export const isAcademicEmail = (email: string): boolean => {
  const normalized = email.toLowerCase().trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) return false;
  return ACADEMIC_EMAIL_SUFFIXES.some((suffix) => normalized.endsWith(suffix));
};
